import 'jquery';
import AbstractController from './AbstractController';
import Controller from './Controller';
import { $window, $html, $body, Breakpoints } from '../constants';

// Utility
import HelperUtility from '../Utility/HelperUtility';

// Service
import HeadlineService from '../Service/HeadlineService';

// HtmlElements
import Banner from '../Elements/Banner';
import BannerCta from '../Elements/BannerCta';
import Header from '../Elements/Header';
import NavSubHorizontal from '../Elements/NavSubHorizontal';
import BackToTopButton from '../Elements/BackToTopButton';
import ContentSlider from '../Elements/ContentSlider';
import Form from '../Elements/Form';
import CtaBar from '../Elements/CtaBar';
import NavMain from '../Elements/NavMain';
import MetaBar from '../Elements/MetaBar';
import Main from '../Elements/Main';
import BigImageText from '../Elements/BigImageText';
import TextAnimation from '../Elements/TextAnimation';
import ReportTable from '../Elements/ReportTable';

/**
 * Controller for this page
 * Handles page flow and special HtmlElements
 *
 * @class
 */
class PageController extends AbstractController {

	/**
	 * @constructor
	 */
	constructor() {
		super();

		Controller.registerController(this);

		// Object containing special elements that inherit from HtmlElement
		// relevant page events are delegated to these objects through the controller
		this._registerElements(
			Banner,
			BannerCta,
			Header,
			NavSubHorizontal,
			BackToTopButton,
			...ContentSlider,
			...Form,
			CtaBar,
			NavMain,
			MetaBar,
			...Main,
			...BigImageText,
			...TextAnimation,
			...ReportTable
		);

		// Initialize HeadlineService
		HeadlineService.initialize();
	}

	// Private

	/**
	 * Adds the javascript for accordions
	 *
	 * @return void
	 */
	_addAccordionJavascript() {
		$('[data-accordion]').each((i, el) => {
			let $accordion = $(el),
				$bigImageText = $accordion.parents('.big-image-text');

			if ($bigImageText.length > 0) {
				let $equalizedRow = $bigImageText.find('> .row'),
					$columns = $equalizedRow.find('> .columns[data-equalizer-watch]');

				$columns.each((i, el) => {
					let $column = $(el),
						equalizedHeight = $column.outerHeight();

					$column.data('equalized-height', equalizedHeight);
				});

				$accordion.on('up.zf.accordion', () => {
					$columns.each((i, el) => {
						let $column = $(el);

						$column.css({
							'height': $column.data('equalized-height'),
							'min-height': ''
						});
					});
				});

				$accordion.on('down.zf.accordion', () => {
					$columns.each((i, el) => {
						let $column = $(el);

						$column.css({
							'height': 'auto',
							'min-height': $column.data('equalized-height')
						});
					});
				});

				$accordion.find('.accordion-title').on('click', () => {
					$columns.each((i, el) => {
						let $column = $(el),
							equalizedHeight = $column.data('equalized-height');

						$column.css('min-height', '');

						if (equalizedHeight > 0) {
							$column.css({
								'height': 'auto',
								'min-height': equalizedHeight
							});
						}
					});
				});
			}
		});
	}

	/**
	 * Adds the anchor scrolling functionality
	 *
	 * @return void
	 */
	_addAnchorScrolling() {
		$body.find('[href*="#"]').each((i, el) => {
			let $element = $(el),
				anchor = $element.get(0).hash;

			if (anchor.length > 1) {
				let $elementToScrollTo = $(anchor);

				if ($elementToScrollTo.length === 1) {
					$element.on('click', (e) => {
						e.preventDefault();
						this._scrollToElement($elementToScrollTo);
					});
				}
			}
		});
	}

	/**
	 * Scrolls the viewport to the given element
	 *
	 * @param { jQuery } $element
	 * @return void
	 */
	_scrollToElement($element) {
		let offset = $element.offset().top;

		if ($window.width() >= Breakpoints.LARGE) {
			offset = offset - Header.getHeight();

			if (NavSubHorizontal.exists()) {
				offset = offset - NavSubHorizontal.getHeight();
			}
		}

		$('html, body').animate({
			scrollTop: offset
		});
	}

	/**
	 * Scrolls automatically to the anchor of the current URL if given and valid
	 *
	 * @return void
	 */
	_scrollToCurrentAnchor() {
		let anchor = window.location.hash;

		if (anchor) {
			let $elementToScrollTo = $(anchor);
			if ($elementToScrollTo.length === 1) {
				$('html, body').animate({
					scrollTop: 0
				}, 0);

				this._scrollToElement($elementToScrollTo);
			}
		}
	}

	// Public

	/**
	 * Handles ready event
	 *
	 * @return void
	 */
	onReady() {
		super.onReady();

		$html.attr('data-touch-device', HelperUtility.isTouchDevice());

		if (!HelperUtility.supportsCssFilter()) {
			$html.addClass('no-css-filter');
		}

		this._addAccordionJavascript();
		this._addAnchorScrolling();
		this.checkRowsContainsBox();
		this._addTableScrollHead();
	}

	/**
	 * Handles load event
	 *
	 * @return void
	 */
	onLoad() {
		this._scrollToCurrentAnchor();
	}

	/**
	 * Checks if equalized rows contain boxes
	 *
	 * @return void
	 */
	checkRowsContainsBox() {
		let $rows = $('.row');

		$rows.each((i, el) => {
			let $row = $(el),
				$columns = $row.find('> .columns');

			$columns.each((i, el) => {
				let $box = $(el).find('.box');

				if ($box.length) {
					$(el).addClass('contains-box');
				}
			});
		});
	}

	_addTableScrollHead() {
		const redRgb = 'rgb(208, 17, 43)';

		$body.find('.table-wrapper').each((i, el) => {
			const $table = $(el).find('table');
			const $thead = $table.find('thead');
			const $theadClone = $thead.clone().addClass('fixed-head');
			const $theadCloneRow = $theadClone.find('tr');
			$table.prepend($theadClone);

			$table.find('thead:not(.fixed-head) tr td').each((i, td) => {
				$theadClone.find('td').eq(i).css('width', $(td).outerWidth());
			});

			$table.find('tbody tr:first-of-type, tbody tr:nth-of-type(2)').each((rowIndex, row) => {
				const $row = $(row);

				$row.find('td').each((tdIndex, td) => {
					const borderTopColor = window.getComputedStyle(td).getPropertyValue('border-top-color');

					if (borderTopColor === redRgb) {
						$(td).addClass('red-border-column');
						$row.addClass('red-border-row');
						$theadCloneRow.find('td').eq(tdIndex).addClass('red-border');
					}
				})
			});

			let distance = $(el).offset().top - $('header').outerHeight();

			$window.on('scroll', () => {
				if ($window.scrollTop() >= distance && $window.scrollTop() < distance + $(el).outerHeight() - $thead.outerHeight()) {
					$body.find('.table-wrapper .fixed-head').not($theadClone).css('display', 'none');

					$theadCloneRow.css('width', $table.outerWidth());

					$theadClone.css({
						display: 'block',
						width: $(el).outerWidth(),
						top: $('header').outerHeight()
					});
				} else {
					$theadClone.css('display', 'none');
				}
			});

			$(el).on('scroll', () => {
				$theadClone.get(0).scrollLeft = $(el).get(0).scrollLeft;
			});

			$window.on('resize', () => {
				distance = $(el).offset().top - $('header').outerHeight();

				$theadClone.css({
					width: $(el).outerWidth()
				});

				$theadCloneRow.css('width', $table.outerWidth());

				$table.find('thead:not(.fixed-head) tr td').each((i, td) => {
					$theadClone.find('td').eq(i).css('width', $(td).outerWidth());
				});
			});
		});
	}
}

export default (new PageController);
